import { LTime } from '@app/components/date/LTime';
import { PlayIcon } from '@app/components/playback/PlayIcon';
import { usePlaybackPlay } from '@app/core/playback/hooks/usePlayback';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { useFragmentCreationStore } from '@app/modules/fragments/stores/fragment-creation.store';
import { copyUrlTooltipMessage } from '@app/modules/logger/screens/stream/components/focus/cursor/cursor-menu/CursorMenuP';
import { getTopicDisplaySummary } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-summary';
import { getTopicDisplayTitle } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-title';
import { useOpenTopicDetailDrawer } from '@app/modules/logger/screens/stream/features/live-topics/hooks/useOpenTopicDetailDrawer';
import { useCopyCursorToClipboard } from '@app/modules/logger/screens/stream/helpers/useCopyCursorToClipboard';
import { nlpIntegration } from '@mi-front/api-clients';
import { toDateTimeStr, toTimestamp } from '@mi-front/core';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import LinkIcon from '@mui/icons-material/Link';
import { Divider, IconButton, Stack, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { useOpenFragmentDrawer } from '../../../../components/focus/selection/useOpenFragmentDrawer';
import { useStreamSelection } from '../../../../stores/useStreamSelection';

const openInPanelMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.popup.openInPanel',
  defaultMessage: 'Ver más detalles',
});

/**
 * Footer for the live topic popup: divider and actions (Play from start, Create fragment with selection and title/summary preset, Copy link with topic start time, Open in panel).
 */
export function LiveTopicPopupFooter({
  topic,
  onClose,
}: {
  topic: nlpIntegration.TopicOutDto;
  onClose?: () => void;
}) {
  const { $t } = useIntl();
  const play = usePlaybackPlay();
  const station = useActiveStation();
  const { setSelection } = useStreamSelection();
  const setFormValues = useFragmentCreationStore((s) => s.setFormValues);
  const openFragmentDrawer = useOpenFragmentDrawer();
  const openTopicDetailDrawer = useOpenTopicDetailDrawer();
  const copyCursorToClipboard = useCopyCursorToClipboard();

  const hasStartTime = !!topic.start_time;
  const hasEndTime = !!topic.end_time;
  const canPlay = hasStartTime;
  const canCreateFragment = hasStartTime && hasEndTime;

  const handlePlay = useCallback(() => {
    if (!hasStartTime) {
      return;
    }

    play({
      startTs: toTimestamp(topic.start_time!),
      playAfterSeek: true,
    });
    onClose?.();
  }, [hasStartTime, topic.start_time, play, onClose]);

  const handleCreateFragment = useCallback(() => {
    if (!canCreateFragment) {
      return;
    }

    const startTs = toTimestamp(topic.start_time!);
    const endTs = toTimestamp(topic.end_time!);
    setSelection(startTs, endTs);
    setFormValues(station.id, {
      title: getTopicDisplayTitle(topic),
      description: getTopicDisplaySummary(topic) ?? '',
    });
    onClose?.();
    openFragmentDrawer();
  }, [
    canCreateFragment,
    topic.start_time,
    topic.end_time,
    topic,
    setSelection,
    setFormValues,
    station.id,
    onClose,
    openFragmentDrawer,
  ]);

  const handleCopyLink = useCallback(() => {
    if (!hasStartTime) {
      return;
    }

    copyCursorToClipboard(station, toTimestamp(new Date(topic.start_time!)));
    onClose?.();
  }, [hasStartTime, topic.start_time, station, onClose, copyCursorToClipboard]);

  const handleOpenInPanel = useCallback(() => {
    openTopicDetailDrawer(topic);
    onClose?.();
  }, [topic, openTopicDetailDrawer, onClose]);

  return (
    <>
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" spacing={1}>
        <Tooltip
          title={$t({
            id: 'common.play',
            defaultMessage: 'Reproducir',
          })}
          placement="top"
        >
          <IconButton size="small" color="secondary" onClick={handlePlay} disabled={!canPlay}>
            <PlayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={$t({
            id: 'logger.streamScreen.selectionMenu.createFragment',
            defaultMessage: 'Crear fragmento',
          })}
          placement="top"
        >
          <IconButton
            size="small"
            color="secondary"
            onClick={handleCreateFragment}
            disabled={!canCreateFragment}
          >
            <FragmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            hasStartTime ? (
              <FormattedMessage
                {...copyUrlTooltipMessage}
                values={{
                  value: (
                    <LTime
                      value={toDateTimeStr(toTimestamp(topic.start_time!))}
                      hour12={false}
                      showSeconds
                    />
                  ),
                }}
              />
            ) : (
              $t(copyUrlTooltipMessage, { value: '' })
            )
          }
          placement="top"
        >
          <IconButton size="small" onClick={handleCopyLink} disabled={!canPlay}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={$t(openInPanelMessage)} placement="top">
          <IconButton size="small" color="secondary" onClick={handleOpenInPanel}>
            <InfoOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </>
  );
}
